import React from "react";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Edit2,
  X, CheckCircle, Loader2, FileText, Send, MessageSquare, Sparkles
} from "lucide-react";
import ChatbotTester from "../ChatbotTester";
import ChatbotStatus from "../ChatbotStatus";
import { useFestivals } from "../landing/BookingCalendar";

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getDateColor = (count, isPast) => {
  if (isPast) return 'bg-gray-100 text-gray-400';
  if (count === 0) return 'bg-green-50 text-green-700 hover:bg-green-100';
  if (count < 3) return 'bg-green-100 text-green-700 hover:bg-green-200';
  if (count < 5) return 'bg-orange-100 text-orange-700 hover:bg-orange-200';
  return 'bg-red-500 text-white';
};

const getDaysInMonth = (date, bookingCounts) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();
  const days = [];
  for (let i = 0; i < startingDay; i++) days.push({ day: null });
  const today = new Date();
  for (let i = 1; i <= daysInMonth; i++) {
    const dayDate = new Date(year, month, i);
    const isPast = dayDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const count = bookingCounts[i] || 0;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    days.push({ day: i, isPast, bookingCount: count, dateStr });
  }
  return days;
};

const BookingsCalendarTab = ({
  auth, authAxios, loading,
  currentMonth, setCurrentMonth, bookingCounts,
  upcomingBookings,
  onAddBooking, onEditBooking, onDeleteBooking,
  onMarkCompleted, onCancelBooking, onResendInvoice, onResendConfirmation
}) => {
  const days = getDaysInMonth(currentMonth, bookingCounts);
  const { byDate: festivalsByDate } = useFestivals(currentMonth.getFullYear());
  const ymPrefix = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
  // Show every festival in the displayed month — past AND future. The
  // emoji markers on day cells come from the same `byDate` map, so users
  // would see marked cells with no legend if we filtered by `today`.
  const festivalsThisMonth = Object.entries(festivalsByDate)
    .filter(([d]) => d.startsWith(ymPrefix))
    .flatMap(([, list]) => list)
    .sort((a, b) => a.date.localeCompare(b.date));

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  return (
    <>
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Calendar Grid */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl shadow-lg p-6" data-testid="admin-calendar">
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => navigateMonth(-1)} className="p-2 hover:bg-[#FFFAF5] rounded-full transition-colors">
                <ChevronLeft size={24} className="text-[#1E1B4B]" />
              </button>
              <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h2>
              <button onClick={() => navigateMonth(1)} className="p-2 hover:bg-[#FFFAF5] rounded-full transition-colors">
                <ChevronRight size={24} className="text-[#1E1B4B]" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-4">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-sm font-semibold text-[#475569] py-2">{day}</div>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="animate-spin text-[#DC283C]" />
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {days.map((dayInfo, index) => {
                  const fest = dayInfo.dateStr ? (festivalsByDate[dayInfo.dateStr] || [])[0] : null;
                  return (
                    <button key={dayInfo.dateStr || `day-${index}`}
                      onClick={() => dayInfo.day && !dayInfo.isPast && dayInfo.bookingCount < 5 && onAddBooking(dayInfo.dateStr)}
                      disabled={!dayInfo.day || dayInfo.isPast || dayInfo.bookingCount >= 5}
                      title={fest ? `${fest.emoji} ${fest.name}` : undefined}
                      className={`
                        aspect-square rounded-xl text-sm font-medium transition-all relative
                        ${!dayInfo.day ? 'invisible' : ''}
                        ${dayInfo.isPast ? 'cursor-not-allowed' : dayInfo.bookingCount < 5 ? 'cursor-pointer hover:ring-2 hover:ring-[#DC283C]' : 'cursor-not-allowed'}
                        ${getDateColor(dayInfo.bookingCount, dayInfo.isPast)}
                        ${fest && !dayInfo.isPast ? 'ring-1 ring-[#DC283C]/40' : ''}
                      `}
                      data-testid={dayInfo.day ? `admin-date-${dayInfo.day}` : undefined}>
                      {dayInfo.day}
                      {fest && (
                        <span
                          className="absolute top-0.5 right-0.5 text-[11px] leading-none pointer-events-none"
                          data-testid={`admin-festival-${dayInfo.dateStr}`}
                        >
                          {fest.emoji}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-4 mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-50 border border-green-200"></div>
                <span className="text-sm text-[#475569]">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-orange-100 border border-orange-200"></div>
                <span className="text-sm text-[#475569]">3-4 Bookings</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="text-sm text-[#475569]">Fully Booked</span>
              </div>
            </div>

            {auth.role !== "security_manager" && (
              <button onClick={() => onAddBooking()}
                className="mt-6 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#DC283C] to-[#FF9F1C] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity"
                data-testid="add-booking-btn">
                <Plus size={20} /> Add New Booking
              </button>
            )}
          </div>
        </div>

        {/* Bookings List */}
        <div className="lg:col-span-1 space-y-6">
          {festivalsThisMonth.length > 0 && (
            <div className="bg-gradient-to-br from-[#FFF0F6] to-[#FFFAF5] rounded-3xl shadow-lg p-6" data-testid="admin-festivals-card">
              <h2 className="text-base font-bold text-[#1E1B4B] font-['Outfit'] mb-3 flex items-center gap-2">
                <Sparkles size={16} className="text-[#DC283C]" /> Festivals this month
              </h2>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {festivalsThisMonth.map((f) => (
                  <div
                    key={`${f.date}-${f.key}`}
                    className="flex items-center gap-3 p-2.5 bg-white/70 rounded-xl"
                    data-testid={`admin-festival-row-${f.key}`}
                  >
                    <span className="text-2xl">{f.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1E1B4B] truncate">{f.name}</p>
                      <p className="text-[11px] text-[#475569]">
                        {new Date(f.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {' · '}
                        <span className={f.behaviour === 'boost' ? 'text-pink-600' : f.behaviour === 'damper' ? 'text-amber-600' : 'text-slate-500'}>
                          {f.behaviour_label}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-3xl shadow-lg p-6" data-testid="bookings-list">
            <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4">
              Upcoming Bookings ({upcomingBookings.length})
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={24} className="animate-spin text-[#DC283C]" />
              </div>
            ) : upcomingBookings.length === 0 ? (
              <p className="text-center text-[#475569] py-10">No upcoming bookings</p>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {upcomingBookings.map((booking) => (
                  <div key={booking.id}
                    className={`p-4 rounded-xl border ${booking.booking_status === 'cancelled' ? 'bg-gray-50 border-gray-200 opacity-60' : booking.booking_status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-[#FFFAF5] border-[#DC283C]/10'}`}
                    data-testid={`booking-item-${booking.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#1E1B4B]">
                            {new Date(booking.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </p>
                          {booking.booking_status === 'completed' && <CheckCircle size={14} className="text-green-600" />}
                          {booking.booking_status === 'completed' && booking.invoice_sent && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-testid={`invoice-sent-${booking.id}`}>
                              <FileText size={10} /> Invoice Sent
                            </span>
                          )}
                        </div>
                        {booking.customer_name && <p className="text-sm text-[#475569] mt-1">{booking.customer_name}</p>}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {booking.package && (
                            <span className="text-xs bg-[#DC283C]/10 text-[#DC283C] px-2 py-1 rounded-full">{booking.package}</span>
                          )}
                          {auth.role !== "security_manager" && booking.discount_percent > 0 && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">{booking.discount_percent}% OFF</span>
                          )}
                          {booking.sales_executive && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{booking.sales_executive}</span>
                          )}
                        </div>
                      </div>
                      {auth.role !== "security_manager" && (
                        <div className="flex flex-col gap-1">
                          <button onClick={() => onEditBooking(booking)} className="p-1.5 hover:bg-white rounded-lg transition-colors">
                            <Edit2 size={14} className="text-[#475569]" />
                          </button>
                          {booking.booking_status !== 'completed' && (
                            <button onClick={() => onMarkCompleted(booking.id)} className="p-1.5 hover:bg-white rounded-lg transition-colors" title="Mark as Completed">
                              <CheckCircle size={14} className="text-green-600" />
                            </button>
                          )}
                          {booking.booking_status === 'completed' && booking.customer_email && (
                            <button onClick={() => onResendInvoice(booking.id)} className="p-1.5 hover:bg-white rounded-lg transition-colors" title="Resend Invoice"
                              data-testid={`resend-invoice-${booking.id}`}>
                              <Send size={14} className="text-blue-500" />
                            </button>
                          )}
                          {booking.customer_phone && (
                            <button onClick={() => onResendConfirmation(booking.id)} className="p-1.5 hover:bg-white rounded-lg transition-colors" title="Resend Confirmation (Email + WhatsApp)"
                              data-testid={`resend-confirmation-${booking.id}`}>
                              <MessageSquare size={14} className="text-green-500" />
                            </button>
                          )}
                          {booking.booking_status !== 'cancelled' && (
                            <button onClick={() => onCancelBooking(booking.id)} className="p-1.5 hover:bg-white rounded-lg transition-colors" title="Cancel Booking"
                              data-testid={`cancel-booking-${booking.id}`}>
                              <X size={14} className="text-orange-500" />
                            </button>
                          )}
                          <button onClick={() => onDeleteBooking(booking.id)} className="p-1.5 hover:bg-white rounded-lg transition-colors">
                            <Trash2 size={14} className="text-red-500" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chatbot Monitor + Tester */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <ChatbotStatus authAxios={authAxios} />
        <ChatbotTester authAxios={authAxios} />
      </div>
    </>
  );
};

export default BookingsCalendarTab;
